import React, { useState, useRef } from "react"
import { useSelector } from "react-redux"
import { addDataLayer } from "../ProductDetailView/analytics"

import { selectPdpState } from "@/store/features/pdpSlice"
import { selectAsPdpState } from "@/store/features/asPdpSlice"
import TabDetails from "./TabDetails"
import TabTechnical from "./TabTechnical"
import FeatureTab from "./FeatureTab"
import SkuInfo from "./SkuInfo"
import TabRecommended from "./TabRecommended"
import usePdpi18n from "@/i18n/usePdpi18n"
import LeadTime from "./LeadTime";

const ProductTabDetail = props => {
  const tabDetailState = useSelector(selectPdpState)
  const pdpStateDetails = useSelector(selectAsPdpState)
  const { asTabDetails: tData } = tabDetailState
  const { tab1, tab2, tab3, tab4, tabContent1, tabContent2, tabContent3, tabContent4, tabDescription4, leadTimeLink } =
    props.authorData
  const { asProductDetails : productDetails } = pdpStateDetails
  const productDetailsString = JSON.stringify(
    window?.adobeDataLayer?.getState?.("page")?.productInfo || {}
  )
  const { productBrandNameDisplay = "" } = tData  
  const {
    availableFileName,
    availableFileNameForSpecification,
    fileName,
    skulabel,
    listlabel,
    specsSheet,
    specification,
    Installation,
    cadDrawings,
    threeDfile,
    twoDplan,
    twoDfront,
    twoDside,
    dwg,
    dxf,
  } = usePdpi18n(props.authorData, "", "", productBrandNameDisplay)
  const tabList = [
    {
      title: tab1,
      tabContent: tabContent1,
    },
    {
      title: tab2,
      tabContent: tabContent2,
    },
    {
      title: tab3,
      tabContent: tabContent3,
    },
    {
      title: tab4,
      tabContent: tabContent4,
    },
  ]

  const category = productDetails?.response?.docs?.length > 0 ? productDetails?.response?.docs[0].ProductLocalCategory_s : ""
  const leadTimeRange = productDetails?.response?.docs?.length > 0 ? productDetails?.response?.docs[0].LeadTimeRange_s
 : ""
 
  const tabsValues = tabList
    .filter(tab => tab.tabContent !== undefined)
    .map(tab => ({
      title: tab.title.toUpperCase(),
      tabContent: tab.tabContent.toUpperCase(),
    }))
  
  const refsArray  =  tabsValues.map(() => useRef(null));
  const tabNavRef = useRef(null);

  const [activeTab, setActiveTab] = useState({
    tab: tabsValues.length > 0 ? tabsValues[0].tabContent : null,
    index: 0,
  })

  if (tData && Object.keys(tData).length !== 0) {
    const {
      isCurated,
      selectedVariantSku,
      priceList,
      productGenericDescription,
      productBrandNameDisplay,
      tabData = {},
    } = tData
    const {
      details = {},
      recommendedUse = {},
      technicalTab = {},
      featureTab = {},
    } = tabData
    
    let leadTime = {
      leadTimeLabel: tabContent4 || "",
      description: tabDescription4 || "",
      leadTimeDocumentURL: leadTimeLink || "",
    }

    const staticTexts = {
      availableFileName,
      availableFileNameForSpecification,
      fileName,
      skulabel,
      listlabel,
      specsSheet,
      specification,
      Installation,
      cadDrawings,
      threeDfile,
      twoDplan,
      twoDfront,
      twoDside,
      dwg,
      dxf,
    }
    const productBrandName = productBrandNameDisplay
      ? category ==="Slabs" ? staticTexts.availableFileNameForSpecification : staticTexts.availableFileName
      : staticTexts.fileName

    const renderTabComponent = tab => {
      switch (tab) {
        case "DETAIL":
        case "DETAILS":
          return (
            <TabDetails
              detailsData={details}
              productBrandName={productBrandName}
              productInfo={productDetailsString}
            />
          )
        case "RECOMMENDEDUSE":
        case "RECOMMENDED USE":
        case "MOREINFORMATION":
        case "MORE INFORMATION":
          return <TabRecommended recommendedUseData={recommendedUse} category={category} />
        case "TECHNICAL":
        case "TECHNICALS":
          return (
            <TabTechnical
              technicalData={technicalTab}
              staticTexts={staticTexts}
              authorData={props.authorData}
              isCurated={isCurated}
              productInfo={productDetailsString}
            />
          )
        case "FEATURE":
        case "FEATURES":
          return (
            <FeatureTab
              featureData={featureTab}
              authorData={props.authorData}
            />
          )

        case "LEADTIME":
        case "LEAD TIME":
          return (
            <LeadTime
              leadTimeData={leadTime} // Lead time data need to get from LW (Need to re-visit here)
              leadTimeRange={leadTimeRange}
            />
          )
        default:
          return null
      }
    }

    const handleTabClick = (tab, index) => {
      setActiveTab({ tab: tab.tabContent, index })
      addDataLayer({
        title: "header tab navigation",
        productInfo: productDetailsString,
        navProps: {
          isNav: true,
          navTitle: tab?.title?.toLowerCase(),
        },
      })
      const element = refsArray[index]
      tabNavRef.current.scrollTo({left:element.current.offsetLeft - 18,behavior: 'smooth'})
    }

    const hasDetails = !!details
    const hasRecommendedUse =
      recommendedUse && recommendedUse.description !== ""
    const hasTechnicalTab =
      !!technicalTab.length &&
      technicalTab.some(
        item =>
          (item.description && item.description.length > 0) ||
          (item.specSheet && item.specSheet.length > 0) ||
          (item.installation && item.installation.length > 0) ||
          (item.threeDRevit && item.threeDRevit.length > 0) ||
          (item.twodDWGPlanView && item.twodDWGPlanView.length > 0) ||
          (item.DXFPlanView && item.DXFPlanView.length > 0) ||
          (item.twodFrontView && item.twodFrontView.length > 0) ||
          (item.DXFFrontView && item.DXFFrontView.length > 0) ||
          (item.twoDSideView && item.twoDSideView.length > 0) ||
          (item.DXFSideView && item.DXFSideView.length > 0) ||
          (item.HomeownersGuide && item.HomeownersGuide.length > 0)
      )
    const hasFeatureTab =
      !!featureTab.length &&
      featureTab.some(item => item.description && item.description.length > 0)

    const hasLeadTime = !!details
    tabsValues.forEach(tab => {
      switch (tab.tabContent) {
        case "DETAIL":
        case "DETAILS":
          tab.hasData = hasDetails
          break
        case "RECOMMENDEDUSE":
        case "RECOMMENDED USE":
          tab.hasData = hasRecommendedUse
          break
        case "TECHNICAL":
        case "TECHNICALS":
          tab.hasData = hasTechnicalTab
          break
        case "FEATURE":
        case "FEATURES":
          tab.hasData = hasFeatureTab
          break
        case "LEAD TIME":
        case "LEADTIME":
          tab.hasData = hasLeadTime // Lead time data need to get from LW (Need to re-visit here)
          break
        default:
          break
      }
    })
    return (
      <div
        className="wrapper"
        id="product-details-tab-details"
        total={tData ? Object.keys(tData).length : 0}
      >
        <div className="wrapper__tab-devider"></div>
        {tabsValues?.length > 0 && (
          <div className="tabWrapper">
            <div className="tabNav">
              <ul
                ref={tabNavRef}
                role="tablist"
                aria-label="Product Details Tabs"
              >
                {tabsValues.map(
                  (tab, index) =>
                    tab.hasData && (
                      <li
                        ref={refsArray[index]}
                        key={index}
                        id={`product-tab-${index}`}
                        role="tab"
                        aria-selected={activeTab.index === index}
                        tabIndex="0"
                        aria-controls={`product-tabpanel-${index}`}
                        onClick={() => handleTabClick(tab, index)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleTabClick(tab, index);
                          }
                        }}
                        className={activeTab.index === index ? "active" : ""}
                      >
                        {(category === "Slabs" && ["RECOMMENDED USE", "RECOMMENDEDUSE"].includes(tab.title)) ?
                          "MORE INFORMATION" : tab.title
                        }
                      </li>
                    )
                )}
              </ul>
            </div>
            <div
              className="tabContent"
              id={`product-tabpanel-${activeTab.index}`}
              role="tabpanel"
              aria-labelledby={`product-tab-${activeTab.index}`}
              tabIndex={0}
            >
              {renderTabComponent(activeTab.tab)}
              {category !== "Slabs" && activeTab.index === 0 && (
                <SkuInfo
                  price={priceList}
                  sku={selectedVariantSku}
                  description={productGenericDescription}
                  skulabel={staticTexts.skulabel}
                  listlabel={staticTexts.listlabel}
                  authorData={props.authorData}
                  productInfo={productDetailsString}
                />
              )}
            </div>
          </div>
        )}
      </div>
    )
  }
  return <p className="shimmer-loading__para shimmer height-30"></p>
}
export default ProductTabDetail
